import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteForever, Download, Edit, PersonOff } from "@mui/icons-material";
import PageTitle from "../components/common/PageTitle";
import ProductManagementGroupsTab from "../components/product-management-groups/ProductManagementGroupsTab";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";

const SETTINGS_TAB_SLUGS = [
  "organization",
  "users",
  "groups",
  "support",
  "data",
] as const;
import { apiService } from "../api/services/apiService";
import { TENANT_ASSIGNABLE_ROLE_OPTIONS } from "../utils/agentDashboardConfig";
import type { TenantMember } from "../types";
import { TenantMemberStatus } from "../types";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

const PURGE_CONFIRMATION_PHRASE = "PURGE ALL TENANT DATA";

const SettingsPage: React.FC = () => {
  const { tabIndex: tab, setTabIndex: setTab } = useBookmarkableTab({
    slugs: SETTINGS_TAB_SLUGS,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [orgLoading, setOrgLoading] = useState(true);
  const [orgSaving, setOrgSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [membersLoading, setMembersLoading] = useState(true);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TenantMember | null>(null);
  const [memberEmail, setMemberEmail] = useState("");
  const [memberDisplayName, setMemberDisplayName] = useState("");
  const [memberRoles, setMemberRoles] = useState<string[]>([]);
  const [memberSaving, setMemberSaving] = useState(false);

  const [days, setDays] = useState(5);
  const [supportLoading, setSupportLoading] = useState(true);
  const [supportSaving, setSupportSaving] = useState(false);

  const [exporting, setExporting] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeConfirmation, setPurgeConfirmation] = useState("");
  const [purging, setPurging] = useState(false);

  const loadOrganization = () => {
    setOrgLoading(true);
    apiService
      .getTenantOrganizationSettings()
      .then((s) => {
        setCompanyName(s.companyName ?? "");
        setAddress(s.address ?? "");
        setWebsite(s.website ?? "");
        setPhone(s.phone ?? "");
        setContactEmail(s.contactEmail ?? "");
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load organization settings"),
      )
      .finally(() => setOrgLoading(false));
  };

  const loadMembers = () => {
    setMembersLoading(true);
    apiService
      .getTenantMembers()
      .then(setMembers)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load members"),
      )
      .finally(() => setMembersLoading(false));
  };

  const loadSupport = () => {
    setSupportLoading(true);
    apiService
      .getTenantSupportCaseSettings()
      .then((s) => setDays(s.autoCloseResolvedDays))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load support settings"),
      )
      .finally(() => setSupportLoading(false));
  };

  useEffect(() => {
    loadOrganization();
    loadMembers();
    loadSupport();
  }, []);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleSaveOrganization = async () => {
    setOrgSaving(true);
    clearMessages();
    try {
      await apiService.updateTenantOrganizationSettings({
        companyName,
        address,
        website,
        phone,
        contactEmail,
      });
      setSuccess("Organization settings saved.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save organization settings");
    } finally {
      setOrgSaving(false);
    }
  };

  const handleSaveSupport = async () => {
    setSupportSaving(true);
    clearMessages();
    try {
      const updated = await apiService.updateTenantSupportCaseSettings(days);
      setDays(updated.autoCloseResolvedDays);
      setSuccess("Support settings saved.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save support settings");
    } finally {
      setSupportSaving(false);
    }
  };

  const openAddMember = () => {
    setEditingMember(null);
    setMemberEmail("");
    setMemberDisplayName("");
    setMemberRoles([]);
    setMemberDialogOpen(true);
  };

  const assignableRoleIds = TENANT_ASSIGNABLE_ROLE_OPTIONS.map((r) => r.id);

  const openEditMember = (member: TenantMember) => {
    setEditingMember(member);
    setMemberEmail(member.email);
    setMemberDisplayName(member.displayName ?? "");
    setMemberRoles(
      member.roles.filter((role) => assignableRoleIds.includes(role)),
    );
    setMemberDialogOpen(true);
  };

  const toggleMemberRole = (role: string) => {
    setMemberRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const handleSaveMember = async () => {
    setMemberSaving(true);
    clearMessages();
    try {
      if (editingMember) {
        await apiService.updateTenantMember({
          email: memberEmail,
          displayName: memberDisplayName,
          roles: memberRoles,
        });
        setSuccess("Member updated.");
      } else {
        await apiService.addTenantMember({
          email: memberEmail,
          displayName: memberDisplayName,
          roles: memberRoles,
        });
        setSuccess("Member added.");
      }
      setMemberDialogOpen(false);
      loadMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save member");
    } finally {
      setMemberSaving(false);
    }
  };

  const handleDeactivateMember = async (email: string) => {
    clearMessages();
    try {
      await apiService.removeTenantMember(email);
      setSuccess("Member deactivated.");
      loadMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to deactivate member");
    }
  };

  const handleExportTenantData = async () => {
    setExporting(true);
    clearMessages();
    try {
      const exportData = await apiService.exportTenantData();
      const blob = new Blob([exportData.json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = exportData.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSuccess(`Exported tenant data to ${exportData.filename}.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to export tenant data");
    } finally {
      setExporting(false);
    }
  };

  const openPurgeDialog = () => {
    setPurgeConfirmation("");
    setPurgeDialogOpen(true);
  };

  const handlePurgeTenantData = async () => {
    setPurging(true);
    clearMessages();
    try {
      const result = await apiService.purgeTenantData(purgeConfirmation.trim());
      setPurgeDialogOpen(false);
      setPurgeConfirmation("");
      setSuccess(result.message);
      loadOrganization();
      loadMembers();
      loadSupport();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to purge tenant data");
    } finally {
      setPurging(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Settings"
        pageDescription="Manage organization contact information, tenant users, roles, support configuration, and data lifecycle."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Tabs value={tab} onChange={setTab} sx={{ mb: 3 }}>
          <Tab label="Organization" />
          <Tab label="Users & roles" />
          <Tab label="Management groups" />
          <Tab label="Support" />
          <Tab label="Data management" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {tab === 0 && (
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            {orgLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Organization contact
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  These fields appear in the site footer for your tenant.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                  <TextField
                    label="Website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Contact email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    fullWidth
                  />
                </Box>
                <Button
                  variant="contained"
                  sx={{ mt: 3 }}
                  onClick={handleSaveOrganization}
                  disabled={orgSaving}
                >
                  {orgSaving ? "Saving..." : "Save organization"}
                </Button>
              </>
            )}
          </Paper>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openAddMember}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add user
              </Button>
            </Box>
            <Paper elevation={0} sx={tablePaperSx}>
              {membersLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                          Roles
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                          Status
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: "text.primary" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={{
                              py: 4,
                              color: (theme) =>
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[300]
                                  : theme.palette.text.secondary,
                              borderBottom: "none",
                            }}
                          >
                            <Typography variant="body2" component="span" sx={{ color: "inherit" }}>
                              No users in this tenant yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        members.map((member) => (
                          <TableRow key={member.email} hover>
                            <TableCell sx={{ color: "text.primary" }}>{member.email}</TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {member.displayName ?? "—"}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {member.roles.map((role) => (
                                  <Chip key={role} label={role} size="small" />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={member.status}
                                size="small"
                                color={
                                  member.status === TenantMemberStatus.ACTIVE
                                    ? "success"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit roles">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditMember(member)}
                                  disabled={member.status !== TenantMemberStatus.ACTIVE}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {member.status === TenantMemberStatus.ACTIVE && (
                                <Tooltip title="Deactivate">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeactivateMember(member.email)}
                                  >
                                    <PersonOff fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}

        {tab === 2 && (
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            <ProductManagementGroupsTab />
          </Paper>
        )}

        {tab === 3 && (
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            {supportLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Support case auto-close
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Resolved cases are automatically moved to Closed after this many days
                  if the creator has not accepted resolution.
                </Typography>
                <TextField
                  type="number"
                  label="Auto-close resolved cases (days)"
                  value={days}
                  onChange={(e) => setDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1, max: 365 }}
                  sx={{ maxWidth: 360 }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  sx={{ mt: 3 }}
                  onClick={handleSaveSupport}
                  disabled={supportSaving}
                >
                  {supportSaving ? "Saving..." : "Save support settings"}
                </Button>
              </>
            )}
          </Paper>
        )}

        {tab === 4 && (
          <Paper sx={{ p: 3, ...tablePaperSx }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Export tenant data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download a JSON snapshot of all CMS content, subscriptions, support cases,
              organization settings, and tenant members for this organization.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExportTenantData}
              disabled={exporting}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {exporting ? "Preparing export..." : "Export all data (JSON)"}
            </Button>

            <Box sx={{ mt: 5, pt: 4, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" sx={{ mb: 1, color: "error.main" }}>
                Purge all tenant data
              </Typography>
              <Alert severity="error" sx={{ mb: 2 }}>
                This action cannot be undone. All products, APIs, categories, documents,
                FAQs, links, media, subscriptions, support cases, teams, groups, roles,
                and non-admin tenant members will be permanently deleted. Tenant
                administrator accounts are preserved, but all of their CMS data will be
                removed.
              </Alert>
              <Alert severity="warning" sx={{ mb: 2 }}>
                We strongly recommend exporting your tenant data before proceeding so you
                have a backup copy.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForever />}
                onClick={openPurgeDialog}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Purge all tenant data
              </Button>
            </Box>
          </Paper>
        )}
      </Container>

      <Dialog
        open={purgeDialogOpen}
        onClose={() => !purging && setPurgeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>Confirm tenant data purge</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is permanent and cannot be undone. All tenant CMS data will be
            deleted. Tenant administrator users will remain, but their data will be gone.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            If you have not already done so, export your data first using the button above.
            To confirm, type{" "}
            <Typography component="span" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
              {PURGE_CONFIRMATION_PHRASE}
            </Typography>{" "}
            below.
          </Typography>
          <TextField
            label="Confirmation phrase"
            value={purgeConfirmation}
            onChange={(e) => setPurgeConfirmation(e.target.value)}
            fullWidth
            autoFocus
            disabled={purging}
            placeholder={PURGE_CONFIRMATION_PHRASE}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeDialogOpen(false)} disabled={purging}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handlePurgeTenantData}
            disabled={purging || purgeConfirmation.trim() !== PURGE_CONFIRMATION_PHRASE}
          >
            {purging ? "Purging..." : "Permanently delete all data"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={memberDialogOpen}
        onClose={() => setMemberDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingMember ? "Edit user roles" : "Add user to tenant"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            type="email"
            value={memberEmail}
            onChange={(e) => setMemberEmail(e.target.value)}
            fullWidth
            required
            disabled={Boolean(editingMember)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Display name"
            value={memberDisplayName}
            onChange={(e) => setMemberDisplayName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Roles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Every member automatically has the <strong>user</strong> role. SAMI X Admin is
            managed by your identity provider and cannot be assigned here.
          </Typography>
          <FormGroup>
            {TENANT_ASSIGNABLE_ROLE_OPTIONS.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={memberRoles.includes(role.id)}
                    onChange={() => toggleMemberRole(role.id)}
                  />
                }
                label={role.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMember}
            disabled={memberSaving || !memberEmail.trim()}
          >
            {memberSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
